import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import Card from "../components/Card";
import TrustedBy from "../components/TrustedBy";
import SecuritySection from "../components/SecuritySection";
import SEO from "../components/SEO";
import StructuredData, {
  createServiceSchema,
  createBreadcrumbSchema,
} from "../components/StructuredData";
import { seoData } from "../utils/seo";

// Viewport settings for better animation triggering
const viewportSettings = {
  once: true,
  amount: 0.1,
  margin: "0px 0px -50px 0px",
};

const Execution = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [activeTab] = useState("modern");

  const slides = [
    {
      image: "/execution/portfolio-dashboard.png",
      description:
        "Estabild generates reports and dashboards that give you a project performance overview by bringing together all key indicators in a single source of truth to help you determine how it would impact your business.",
    },
    {
      image: "/execution/project-dashboard.png",
      description:
        "See all key performance indicators in one place to help you better understand the project status. The dashboard gives you an overview of major risk events, status of work completed, and forecast values for cost, schedule and cashflow.",
    },
    {
      image: "/execution/project-report.png",
      description:
        "Reduce time spent in creating manual reports and let Estabild to do it for you. You can use built-in reports or customize them according to the recipient needs. Download reports from the platform and share with your key stakeholders.",
    },
  ];

  const benefits = [
    {
      title: "Automated project reports",
      image: "/execution/automated-reports.png",
      points: [
        "Reports are automatically generated in a matter of seconds.",
        "Share them with internal and external stakeholders.",
        "Download prepared reports to PDF documents.",
      ],
    },
    {
      title: "Customizable reports to match your needs",
      image: "/execution/customizable-reports.png",
      points: [
        "Define what information is included in a report.",
        "Add report notes and remarks.",
        "Setup when a report should be delivered.",
      ],
    },
    {
      title: "Performance overview with summary dashboards",
      image: "/execution/project-performance.png",
      points: [
        "Project-level dashboard summarizes all key performance metrics in one view.",
        "Compare actual results against the business plan.",
        "Dashboards can be customized according to your needs and preferences.",
      ],
    },
    {
      title: "Proactive performance portfolio overview and forecasting",
      image: "/execution/portfolio-performance.png",
      points: [
        "Standardize all projects with the same reporting framework.",
        "Get a complete overview of portfolio projects.",
        "Map all projects at different stages to identify the best opportunities and do a comparative analysis.",
      ],
    },
    {
      title: "Budget allocation and forcasting",
      image: "/execution/budget-allocation.png",
      points: [
        "Allocate budget to committed contracts and track spending.",
        "Compare project budget against actual cost.",
        "Manage cash flows and forecasts on live projects.",
      ],
    },
    {
      title: "Change order management",
      image: "/execution/change-order.png",
      points: [
        "Keep track of approved change orders.",
        "Get accurate liquuidity overview.",
        "Anticipate potential changes caused by identified risks.",
      ],
    },
    {
      title: "Track progress of project activities",
      image: "/execution/progress-activities.png",
      points: [
        "Get complete overview of major activities, critical path and available float.",
        "Enable dynamic relationships among project items.",
        "Automate progress tracking.",
      ],
    },
    {
      title: "Validate scope of work complete",
      image: "/execution/validate-work.png",
      points: [
        "Have a side-by-side comparison of progress between scheduled activities and related budget accounts.",
        "Validate invoice payments.",
        "Consolidate verification documents for the completed work.",
      ],
    },
    {
      title: "Proactive risk management",
      image: "/execution/risk-management.png",
      points: [
        "Manage development risk register.",
        "Risk ranking based on a qualitative assessment.",
        "Risk categorization by function type.",
      ],
    },
    {
      title: "Centralized project updates",
      image: "/execution/centralized-updates.png",
      points: [
        "Active collaboration between stakeholders to ensure you won't ever miss a major update.",
        "Get status progress updates in a simple and convenient way.",
        "Receive timely notifications as soon as progress updates are provided.",
      ],
    },
  ];

  const integrations = [
    { name: "Fortnox", image: "/integrations/fortnox.png" },
    { name: "MS Project", image: "/integrations/ms-project.png" },
    { name: "Oracle", image: "/integrations/oracle.png" },
    { name: "Excel", image: "/integrations/excel.png" },
    { name: "Visma", image: "/integrations/visma.png" },
    { name: "Outlook", image: "/integrations/outlook.png" },
    { name: "QuickBooks", image: "/integrations/quickbooks.png" },
    { name: "Google Drive", image: "/integrations/gdrive.png" },
    { name: "SharePoint", image: "/integrations/sharepoint.png" },
    { name: "SAP", image: "/integrations/sap.png" },
    { name: "PDF", image: "/integrations/pdf.png" },
    { name: "Internal Tool", image: "/integrations/internal-tool.png" },
    { name: "Sage", image: "/integrations/sage.png" },
    { name: "Gmail", image: "/integrations/gmail.png" },
  ];

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const executionSeo = seoData.execution;
  const structuredData = [
    createServiceSchema(
      "Project Execution Management",
      executionSeo.description,
      executionSeo.url
    ),
    createBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Solutions", url: "/#solutions" },
      { name: "Execution", url: executionSeo.url },
    ]),
  ];

  return (
    <div className="min-h-screen pt-20 sm:pt-24">
      <SEO
        title={executionSeo.title}
        description={executionSeo.description}
        keywords={executionSeo.keywords}
        url={executionSeo.url}
      />
      <StructuredData data={structuredData} />
      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-white">
        <div className="container-custom px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-xs sm:text-sm uppercase tracking-wider text-primary-600 font-semibold mb-3 sm:mb-4">
                Modern software for Construction developers
              </h2>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
                Complete overview of your projects to drive better decisions
              </h1>
              <div className="mt-6 sm:mt-8">
                <Link to="/contact">
                  <Button
                    variant="primary"
                    size="large"
                    className="w-full sm:w-auto"
                  >
                    Book free demo
                  </Button>
                </Link>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-12"
            >
              <img
                src="/execution/hero-image.png"
                alt="Execution Dashboard"
                className="w-full"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Trusted By Section */}
      <TrustedBy />
      <section className="py-16 sm:py-20">
        <div className="container-custom px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 text-center">
            Single place for your projects reporting
          </h2>

          {/* Slider Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="max-w-5xl mx-auto mt-6"
          >
            <div className="relative px-8 sm:px-0">
              {/* Image Container with fixed aspect ratio */}
              <div className="relative">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={activeSlide}
                    src={slides[activeSlide].image}
                    alt={`Dashboard ${activeSlide + 1}`}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.3 }}
                    className="w-full"
                  />
                </AnimatePresence>
              </div>

              {/* Description Container with fixed height */}
              <div className="relative h-32 sm:h-28 mb-6">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={activeSlide}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="text-sm sm:text-base lg:text-md text-gray-600 max-w-3xl mx-auto text-center absolute inset-0 flex items-center justify-center px-4"
                  >
                    {slides[activeSlide].description}
                  </motion.p>
                </AnimatePresence>
              </div>

              {/* Slider Controls */}
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/3 -translate-y-1/2 -translate-x-8 sm:-translate-x-4 lg:-translate-x-12 bg-white rounded-full p-2 sm:p-3 shadow-lg hover:shadow-xl transition-all duration-200 z-10"
                aria-label="Previous slide"
              >
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/3 -translate-y-1/2 translate-x-8 sm:translate-x-4 lg:translate-x-12 bg-white rounded-full p-2 sm:p-3 shadow-lg hover:shadow-xl transition-all duration-200 z-10"
                aria-label="Next slide"
              >
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              {/* Slider Dots */}
              <div className="flex justify-center gap-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveSlide(index)}
                    className={`transition-all duration-200 rounded-full ${
                      index === activeSlide
                        ? "w-3 h-3 bg-primary-600"
                        : "w-2 h-2 bg-gray-400"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modern Way */}
      <section className="pb-20">
        <div className="container-custom">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="max-w-6xl mx-auto"
            >
              <img
                src="/execution/integrated-platform.png"
                alt="Integrated Platform"
                className="w-full"
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-8 sm:mb-12 px-4">
              Experience these benefits for your business
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportSettings}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <Card hover>
                  <img
                    src={benefit.image}
                    alt={benefit.title}
                    className="w-full rounded-lg mb-4 sm:mb-6 bg-gray-100"
                  />
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                    {benefit.title}
                  </h3>
                  <ul className="space-y-2 sm:space-y-3">
                    {benefit.points.map((point, pointIndex) => (
                      <li
                        key={pointIndex}
                        className="flex items-start gap-2 sm:gap-3"
                      >
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
                        <p className="text-gray-600 text-sm sm:text-base">
                          {point}
                        </p>
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="pb-16 sm:pb-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
            className="max-w-7xl mx-auto"
          >
            <Card variant="dark" className="text-center p-8 sm:p-12 lg:p-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
                Seamless integration with your tools
              </h2>
              <p className="text-sm sm:text-base lg:text-md text-gray-300 mb-8 sm:mb-12">
                No need for big changes in your workflow nor steep learning
                curves for your team.
                <br className="hidden sm:block" />
                We will ensure a smooth transition to Estabild and support you
                along the way.
              </p>
              <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-4 sm:gap-6 items-center justify-items-center">
                {integrations.map((integration, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={viewportSettings}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="hover:scale-110 transition-transform duration-200"
                  >
                    <img
                      src={integration.image}
                      alt={integration.name}
                      className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 object-contain"
                    />
                  </motion.div>
                ))}
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Security Section */}
      <SecuritySection />

      {/* Final CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="container-custom text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8">
              Focus on your projects growth.
              <br />
              We'll take care of the reporting.
            </h2>
            <Link to="/contact">
              <Button variant="ghost" size="large" className="w-full sm:w-auto">
                Book demo
              </Button>
            </Link>
            <div className="mt-8 sm:mt-12">
              <p className="text-xs sm:text-sm uppercase tracking-wider text-primary-200 mb-2">
                Email
              </p>
              <a
                href="mailto:hello@estabild.com?subject=Interest%20in%20Estabild"
                className="text-lg sm:text-xl lg:text-2xl font-semibold hover:text-primary-100 transition-colors"
              >
                hello@estabild.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Execution;
